import { DocumentEditorProvider } from "../../components/document-editor/DocumentEditorProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { api } from "../../api";
import { downloadConversation } from "./downloadConversation";
import { ConversationWorkspace } from "./ConversationWorkspace";
import { applyQueueSnapshot } from "./conversationQueueState";
import {
  Composer,
  type ComposerActivity,
  type ComposerOptions,
} from "../../components/composer/Composer";
import { ConfirmDangerDialog } from "../../components/shared/ConfirmDangerDialog";
import { ChatMentionsProvider } from "../../components/chat/ChatMentions";
import { ChatDevelopmentPanel } from "../../components/chat/ChatDevelopmentPanel";
import { ConfirmMessageDeleteDialog } from "../../components/chat/ConfirmMessageDeleteDialog";
import { MessageCard } from "../../components/chat/MessageCard";
import { ModelSearchPicker } from "../../components/composer/ModelSearchPicker";
import { RenameDialog } from "../../components/shared/RenameDialog";
import { ShareDialog } from "../../components/ShareDialog";
import { TodoPanel } from "../../components/chat/TodoPanel";
import { GoalPanel } from "../../components/chat/GoalPanel";
import {
  MessageQueuePanel,
  type QueuedMessage,
} from "../../components/chat/MessageQueuePanel";
import {
  latestGoal,
  modelSupportsVision,
  type Attachment,
} from "@lumen/shared";
import { t } from "../../i18n";
import { editFocusTimeline } from "./compactionTimeline";
import { delegatedActivityModelIdsFrom, modelVoices } from "./ChatPage.utils";
import type { ChatPageViewProps } from "./ChatContextTypes";
import {
  ArrowUp,
  AudioLines,
  Bot,
  Boxes,
  Columns3,
  Download,
  ImagePlus,
  LoaderCircle,
  Mic,
  Music,
  Palette,
  Pencil,
  Share2,
  Sparkles,
  Trash2,
  Video,
} from "lucide-react";

export function ChatPageView({
  props,
  onToggleSidebar,
}: {
  props: ChatPageViewProps;
  onToggleSidebar?: () => void;
}) {
  const location = useLocation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<{
    conversationId: string;
    message: string;
  } | null>(null);
  const { id: routeConversationId } = useParams();
  const editorOpen = /\/workspace\/?$/.test(location.pathname);
  const [contextInsertion, setContextInsertion] = useState<
    { id: number; text: string } | undefined
  >();
  const [editorSelection, setEditorSelection] = useState<{
    workspaceId: string | null;
    repositoryId: string | null;
  } | null>(null);
  useEffect(() => {
    setEditorSelection(null);
  }, [routeConversationId]);
  const composerDraft = useRef<{ conversationId?: string; text: string }>({
    text: "",
  });
  const contextSequence = useRef(0);
  const composerAttachments = useRef<{
    conversationId?: string;
    files: Attachment[];
  }>({ files: [] });
  // While a message is being edited, the edit composer replaces the send
  // composer and later messages stay hidden until the edit is saved or
  // cancelled.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const consumeContextInsertion = useCallback((id: number) => {
    setContextInsertion((current) =>
      current?.id === id ? undefined : current,
    );
  }, []);
  const {
    activity,
    selectedModelId,
    changeModel,
    activityModels,
    preferences,
    setPreferences,
    selected,
    setActivityDefault,
    temporary,
    conversation,
    setConversation,
    developmentOpen,
    setDevelopmentOpen,
    sending,
    compacting,
    actionBusy,
    openRename,
    openShare,
    setDeleteConversationOpen,
    compactConversation,
    scrollToTop,
    showScrollTop,
    handleScroll,
    handleWheel,
    scrollArea,
    scrollContent,
    loadingConversation,
    loadingOlder,
    modelLookup,
    models,
    configuredRepositoryId,
    agentLookup,
    agents,
    user,
    activityModelOptions,
    decidingApprovalIds,
    approvalsById,
    decideApproval,
    answerQuestion,
    edit,
    regenerate,
    continueResponse,
    continueSubagent,
    chooseDeleteTarget,
    selectVersion,
    error,
    reconnectGeneration,
    todoList,
    send,
    stopGeneration,
    setDevelopmentConfigured,
    agentId,
    selectAgent,
    composerInitialOptions,
    developmentConfigurationLoaded,
    changeActivity,
    availableActivities,
    activityModelSelection,
    activitySettings,
    enabledActivities,
    activityModelIds,
    setActivityModelSelection,
    activitySettingsRef,
    setActivitySettings,
    setEnabledActivities,
    setActivity,
    thinkingLevel,
    setThinkingLevel,
    deleteTarget,
    remove,
    renameOpen,
    titleDraft,
    setTitleDraft,
    renameConversation,
    shareOpen,
    shareInfo,
    setDeleteTarget,
    setRenameOpen,
    setShareOpen,
    shareUrl,
    enableShare,
    disableShare,
    deleteConversationOpen,
    deleteConversation,
    panelWorkspaceId,
    panelRepositoryId,
    developmentConfigured,
    usageRevision,
  } = props;
  const [queueEditError, setQueueEditError] = useState("");
  const [queueSaving, setQueueSaving] = useState(false);
  const [queueEdit, setQueueEdit] = useState<QueuedMessage | null>(null);
  const [queueEditActivity, setQueueEditActivity] =
    useState<ComposerActivity>("ask");
  const [queueEditAgentId, setQueueEditAgentId] = useState("");
  const [queueEditEnabledActivities, setQueueEditEnabledActivities] = useState<
    ComposerActivity[]
  >(["ask"]);
  const [queueEditModelIds, setQueueEditModelIds] = useState(
    activityModelSelection,
  );
  const [queueEditSettings, setQueueEditSettings] = useState(activitySettings);
  const [queueEditThinking, setQueueEditThinking] =
    useState<typeof thinkingLevel>("");
  const beginQueueEdit = (item: QueuedMessage) => {
    const options = (item.options ?? {}) as Partial<ComposerOptions>;
    setQueueEdit(item);
    setQueueEditError("");
    setQueueEditActivity(
      (options.generationParameters?.activity as ComposerActivity) ?? "ask",
    );
    setQueueEditAgentId(
      options.agentId === undefined
        ? (conversation?.agentId ?? "")
        : (options.agentId ?? ""),
    );
    setQueueEditEnabledActivities([
      "ask",
      ...((options.generationParameters?.enabledActivities ??
        []) as ComposerActivity[]),
    ]);
    setQueueEditModelIds(
      (options.generationParameters
        ?.activityModelIds as typeof activityModelSelection) ?? {},
    );
    setQueueEditSettings(
      (options.generationParameters
        ?.activitySettings as typeof activitySettings) ?? {},
    );
    setQueueEditThinking(options.thinkingLevel ?? "");
  };
  const queueItems = useMemo(
    () =>
      Array.isArray(conversation?.composerDefaults?.queue)
        ? (conversation.composerDefaults.queue as QueuedMessage[])
        : [],
    [conversation?.composerDefaults?.queue],
  );
  // Changes whenever a message or its tool activity changes, so the side
  // panel re-checks for artifacts without refetching on streaming deltas.
  const conversationRevision = useMemo(
    () =>
      conversation
        ? [
            conversation.id,
            conversation.messages?.length ?? 0,
            conversation.messages?.at(-1)?.id ?? "",
            conversation.messages?.reduce(
              (sum, message) => sum + (message.toolEvents?.length ?? 0),
              0,
            ),
          ].join(":")
        : "",
    [conversation],
  );
  useEffect(() => {
    setQueueEdit(null);
  }, [conversation?.id]);
  useEffect(() => {
    // Stop editing when the conversation changes or the edited message leaves
    // the active branch (deleted, or replaced after a version switch).
    if (editingMessageId) {
      const messages = conversation?.messages ?? [];
      if (!messages.some((message) => message.id === editingMessageId))
        setEditingMessageId(null);
    }
  }, [conversation, editingMessageId]);
  useEffect(() => {
    if (queueEdit && !queueItems.some((item) => item.id === queueEdit.id))
      setQueueEdit(null);
  }, [queueEdit, queueItems]);
  const queueEditModelId =
    queueEditActivity === "ask"
      ? (agentLookup.get(queueEditAgentId)?.modelId ?? conversation?.modelId)
      : (queueEditSettings[queueEditActivity]?.modelId ??
        queueEditModelIds[queueEditActivity]);
  const queueEditModel =
    modelLookup.get(queueEditModelId ?? "") ??
    (queueEditActivity !== "ask"
      ? activityModelOptions[queueEditActivity]?.find(
          (model) => model.id === queueEditModelId,
        )
      : undefined);
  const canManageConversation =
    !conversation || conversation.userId === user.id;
  const exportLabel = exporting
    ? t("Exporting conversation…")
    : t("Export conversation");
  const visibleExportError =
    exportError?.conversationId === conversation?.id
      ? exportError?.message
      : undefined;
  const exportConversation = async () => {
    if (!conversation || exporting) return;
    const conversationId = conversation.id;
    setExporting(true);
    setExportError(null);
    try {
      await downloadConversation(conversationId);
    } catch (cause) {
      setExportError({
        conversationId,
        message:
          cause instanceof Error
            ? cause.message
            : t("Could not export conversation"),
      });
    } finally {
      setExporting(false);
    }
  };
  const chatPanel = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex h-14 min-w-0 shrink-0 items-center gap-3 border-b border-stone-200 px-3 sm:px-5 dark:border-white/10">
        <div
          className="min-w-0 shrink"
          title={
            activity === "ask"
              ? t(
                  "This is the chat model. To choose a delegated activity model, switch to that activity, select its model, then switch back to Ask.",
                )
              : t("This model handles the selected activity.")
          }
        >
          <ModelSearchPicker
            models={activityModels}
            value={selectedModelId}
            onChange={changeModel}
            preferences={preferences}
            onPreferencesChange={setPreferences}
            hideUniformCapabilityFilters
            defaultModelId={
              activity === "ask"
                ? (preferences?.activityDefaultModelIds?.ask ??
                  preferences?.defaultModelId ??
                  preferences?.adminDefaultModelId ??
                  null)
                : (preferences?.activityDefaultModelIds?.[activity] ??
                  preferences?.adminDefaultModelId ??
                  null)
            }
            onSetDefault={(id) => setActivityDefault(activity, id)}
            setDefaultLabel={
              activity === "ask"
                ? t("Set as default for Ask")
                : t("Set as default for this mode")
            }
          />
        </div>
        {temporary ? (
          <span className="ml-auto text-xs font-medium text-stone-500">
            {t("Temporary chat · deleted when closed")}
          </span>
        ) : (
          conversation && (
            <>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {!editorOpen && (
                  <button
                    type="button"
                    onClick={() => setDevelopmentOpen((open) => !open)}
                    className={`button p-2 ${developmentOpen ? "text-lumen-700 dark:text-lumen-300" : ""}`}
                    title={t("Open side panel")}
                    aria-label={t("Open side panel")}
                  >
                    <Columns3 size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void compactConversation()}
                  disabled={sending || compacting || actionBusy}
                  className={`button p-2 ${compacting ? "text-lumen-700 dark:text-lumen-300" : ""}`}
                  title={t("Compact conversation")}
                  aria-label={t("Compact conversation")}
                >
                  <Sparkles
                    size={16}
                    className={compacting ? "animate-pulse" : ""}
                  />
                </button>
                {canManageConversation && (
                  <>
                    <button
                      type="button"
                      onClick={() => void exportConversation()}
                      disabled={exporting || sending || compacting}
                      aria-busy={exporting}
                      aria-label={exportLabel}
                      title={exportLabel}
                      className="button p-2"
                    >
                      {exporting ? (
                        <LoaderCircle size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={openRename}
                      disabled={sending || compacting}
                      className="button p-2"
                      title={t("Rename chat")}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void openShare()}
                      disabled={sending || compacting}
                      className={`button p-2 ${conversation.shared ? "text-lumen-600 dark:text-lumen-300" : ""}`}
                      title={t("Share chat")}
                    >
                      <Share2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConversationOpen(true)}
                      disabled={sending || compacting}
                      className="button p-2 text-stone-400 hover:text-red-600"
                      title={t("Delete chat")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </>
          )
        )}
      </div>
      <div className="relative min-h-0 min-w-0 flex-1">
        {showScrollTop && (
          <button
            type="button"
            onClick={scrollToTop}
            className="absolute top-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-stone-200 bg-white/95 px-3.5 py-2 text-xs font-semibold text-stone-600 shadow-lg backdrop-blur transition hover:border-lumen-300 hover:bg-lumen-50 hover:text-lumen-700 dark:border-white/10 dark:bg-[#171b1a]/95 dark:text-stone-200 dark:hover:border-lumen-700 dark:hover:bg-lumen-950/50 dark:hover:text-lumen-300"
            aria-label={t("Scroll to the top")}
          >
            <ArrowUp size={14} />
            {t("Scroll to the top")}
          </button>
        )}
        <div
          data-chat-scroll=""
          ref={scrollArea}
          onScroll={handleScroll}
          onWheel={handleWheel}
          className="h-full min-h-0 min-w-0 overflow-x-hidden overflow-y-auto"
        >
          <div ref={scrollContent} className="min-h-full min-w-0">
            {loadingConversation ? (
              <div className="flex h-full items-center justify-center gap-3 text-sm text-stone-400">
                <LoaderCircle
                  className="animate-spin text-lumen-600"
                  size={20}
                />{" "}
                {t("Loading conversation…")}
              </div>
            ) : !conversation?.messages?.length ? (
              <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center px-6 pt-10 pb-20 text-center">
                <div className="mb-5 grid size-14 place-items-center rounded-2xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
                  {activity === "image" ? (
                    <Palette size={26} />
                  ) : activity === "video" ? (
                    <Video size={26} />
                  ) : activity === "audio" ? (
                    <Music size={26} />
                  ) : activity === "speech" ? (
                    <Mic size={26} />
                  ) : activity === "transcription" ? (
                    <AudioLines size={26} />
                  ) : activity === "model3d" ? (
                    <Boxes size={26} />
                  ) : activity === "sprite" ? (
                    <ImagePlus size={26} />
                  ) : activity === "tileset" ? (
                    <Boxes size={26} />
                  ) : (
                    <Bot size={26} />
                  )}
                </div>
                <h1 className="text-3xl font-bold tracking-tight">
                  {activity === "image"
                    ? t("What image should we create?")
                    : activity === "video"
                      ? t("What video should we create?")
                      : activity === "audio"
                        ? t("What audio should we create?")
                        : activity === "speech"
                          ? t("What should we say?")
                          : activity === "transcription"
                            ? t("What should we transcribe?")
                            : activity === "model3d"
                              ? t("What 3D model should we create?")
                              : activity === "sprite"
                                ? t("What sprite should we create?")
                                : activity === "tileset"
                                  ? t("What tileset should we create?")
                                  : t("Where should we begin?")}
                </h1>
                <p className="mt-3 max-w-md text-sm leading-6 text-stone-500">
                  {activity === "image"
                    ? t(
                        "Describe your image, add a reference if you like, and refine it here together.",
                      )
                    : activity === "video"
                      ? t(
                          "Describe the scene, motion, style, and framing you want.",
                        )
                      : activity === "audio"
                        ? t(
                            "Describe the music, sound, or audio you want to generate.",
                          )
                        : activity === "speech"
                          ? t("Enter text and choose an available audio model.")
                          : activity === "transcription"
                            ? t(
                                "Attach an audio or video file to turn it into text.",
                              )
                            : activity === "model3d"
                              ? t(
                                  "Describe a game-ready prop, character, or environment asset.",
                                )
                              : activity === "sprite"
                                ? t(
                                    "Describe the character, prop, VFX, or sprite-sheet style you need.",
                                  )
                                : activity === "tileset"
                                  ? t(
                                      "Describe the terrain, walls, floors, and tile layout for the game.",
                                    )
                                  : t(
                                      "Choose an available model, write with Markdown, or paste an image directly into the composer.",
                                    )}
                </p>
                {models.length === 0 && (
                  <p className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                    {t(
                      "No models are available. Ask an administrator to configure and sync a provider.",
                    )}
                  </p>
                )}
              </div>
            ) : (
              <>
                {loadingOlder && (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs text-stone-400">
                    <LoaderCircle
                      className="animate-spin text-lumen-600"
                      size={14}
                    />{" "}
                    {t("Loading earlier messages…")}
                  </div>
                )}
                {editFocusTimeline(conversation.messages, editingMessageId).map(
                  (message) => (
                    <MessageCard
                      key={message.id}
                      message={message}
                      repositoryId={
                        configuredRepositoryId !== undefined
                          ? configuredRepositoryId
                          : localStorage.getItem("lumen:active-repository") ||
                            null
                      }
                      model={
                        modelLookup.get(message.modelId ?? "") ??
                        modelLookup.get(message.modelRemoteId ?? "") ??
                        modelLookup.get(message.modelName ?? "") ??
                        selected
                      }
                      agentName={
                        message.agentId
                          ? agentLookup.get(message.agentId)?.name
                          : undefined
                      }
                      agents={agents}
                      user={user}
                      activityModels={activityModelOptions}
                      busy={sending || compacting}
                      decidingApprovalIds={decidingApprovalIds}
                      approvals={approvalsById}
                      onApproval={decideApproval}
                      onQuestionAnswer={answerQuestion}
                      onEdit={edit}
                      onRegenerate={regenerate}
                      onContinue={continueResponse}
                      onSubagentContinue={continueSubagent}
                      onDelete={chooseDeleteTarget}
                      onSelectVersion={selectVersion}
                      editing={editingMessageId === message.id}
                      onEditingChange={(editing) =>
                        setEditingMessageId(editing ? message.id : null)
                      }
                    />
                  ),
                )}
              </>
            )}
            {sending &&
              conversation?.messages?.at(-1)?.role !== "assistant" && (
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-16 py-4 text-sm text-stone-400">
                  <LoaderCircle
                    className="animate-spin text-lumen-600"
                    size={17}
                  />{" "}
                  {t("Thinking with {{model}}…", {
                    model: selected?.name ?? "",
                  })}
                </div>
              )}
            {(error || visibleExportError) && (
              <div
                className="mx-auto mb-3 flex max-w-3xl items-center justify-between gap-3 px-4 text-sm text-red-600"
                role="alert"
              >
                {error || visibleExportError}
                {error ===
                  "Live updates are temporarily unavailable. The generation continues in the background." && (
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/40"
                    data-testid="generation-reconnect"
                    onClick={reconnectGeneration}
                  >
                    Reconnect
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {todoList && todoList.completed < todoList.total && (
        <TodoPanel snapshot={todoList} />
      )}
      {(() => {
        if (editingMessageId || !conversation) return null;
        const goal = latestGoal(conversation.messages ?? [], conversation.goal);
        if (!goal) return null;
        const last = conversation.messages?.at(-1);
        return (
          <GoalPanel
            goal={goal}
            running={sending}
            waiting={
              conversation.generationStatus === "awaiting-question" ||
              conversation.generationStatus === "awaiting-approval"
            }
            failed={conversation.generationStatus === "failed"}
            onStop={stopGeneration}
            onContinue={
              last?.role === "assistant"
                ? () => void continueResponse(last)
                : undefined
            }
          />
        );
      })()}
      {conversation && (
        <MessageQueuePanel
          key={`queue:${conversation.id}`}
          conversationId={conversation.id}
          items={queueItems}
          onChange={(queue, revision) => {
            setConversation((current) =>
              applyQueueSnapshot(current, conversation.id, queue, revision),
            );
          }}
          onEdit={beginQueueEdit}
          editor={
            queueEdit && (
              <div
                className="fixed inset-0 z-50 overflow-y-auto bg-black/30 px-4 py-8"
                role="dialog"
                aria-label={t("Edit queued message")}
              >
                <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-xl dark:bg-[#1b201e]">
                  {queueEditError && (
                    <div
                      role="alert"
                      className="px-4 pt-4 text-sm text-red-600"
                    >
                      {queueEditError}
                    </div>
                  )}
                  <Composer
                    key={queueEdit.id}
                    mode="edit"
                    editLabel={t("Save queued message")}
                    disabled={queueSaving}
                    initialValue={queueEdit.content}
                    initialAttachments={queueEdit.attachments ?? []}
                    initialOptions={
                      queueEdit.options as Partial<ComposerOptions>
                    }
                    activity={queueEditActivity}
                    onActivityChange={setQueueEditActivity}
                    availableActivities={availableActivities}
                    activityModels={activityModelOptions}
                    activityModelIds={queueEditModelIds}
                    enabledActivities={queueEditEnabledActivities}
                    activitySettings={queueEditSettings}
                    onActivityModelChange={(candidate, modelId) =>
                      setQueueEditModelIds((current) => ({
                        ...current,
                        [candidate]: modelId,
                      }))
                    }
                    onActivitySettingsChange={(candidate, settings) =>
                      setQueueEditSettings((current) => ({
                        ...current,
                        [candidate]: settings ?? {},
                      }))
                    }
                    onToggleActivity={(candidate) =>
                      setQueueEditEnabledActivities((current) =>
                        current.includes(candidate)
                          ? current.filter((item) => item !== candidate)
                          : [...current, candidate],
                      )
                    }
                    onSend={async (content, attachments, options) => {
                      setQueueSaving(true);
                      setQueueEditError("");
                      try {
                        const result = await api<{
                          queue: QueuedMessage[];
                          revision: number;
                        }>(
                          `/conversations/${conversation.id}/queue/${queueEdit.id}`,
                          {
                            method: "PATCH",
                            body: JSON.stringify({
                              content,
                              options: {
                                ...options,
                                generationParameters: {
                                  ...options.generationParameters,
                                  activityModelIds:
                                    delegatedActivityModelIdsFrom(
                                      queueEditModelIds,
                                    ),
                                },
                              },
                              attachments,
                              attachmentIds: attachments.map(
                                (attachment) => attachment.id,
                              ),
                            }),
                          },
                        );
                        setConversation((current) =>
                          applyQueueSnapshot(
                            current,
                            conversation.id,
                            result.queue,
                            result.revision,
                          ),
                        );
                        setQueueEdit(null);
                      } catch (reason) {
                        setQueueEditError(
                          reason instanceof Error
                            ? reason.message
                            : t("Could not update queue"),
                        );
                        throw reason;
                      } finally {
                        setQueueSaving(false);
                      }
                    }}
                    onCancel={() => setQueueEdit(null)}
                    agents={agents}
                    agentId={queueEditAgentId}
                    onAgentChange={setQueueEditAgentId}
                    developmentWorkspaceLoaded={developmentConfigurationLoaded}
                    developmentConfigured={developmentConfigured}
                    conversationId={conversation.id}
                    supportsVision={
                      queueEditModel
                        ? modelSupportsVision(queueEditModel)
                        : false
                    }
                    supportsTools={Boolean(
                      queueEditModel?.capabilities.tools &&
                      queueEditModel.toolsEnabled,
                    )}
                    thinkingLevels={queueEditModel?.thinkingLevels}
                    supportsThinking={queueEditModel?.supportedParameters.some(
                      (parameter) =>
                        parameter === "reasoning" ||
                        parameter === "reasoning_effort",
                    )}
                    supportedParameters={
                      queueEditModel?.supportedParameters ?? []
                    }
                    availableVoices={
                      queueEditModel ? modelVoices(queueEditModel) : []
                    }
                    thinkingLevel={queueEditThinking}
                    onThinkingLevelChange={setQueueEditThinking}
                  />
                </div>
              </div>
            )
          }
        />
      )}
      {/* While a message is being edited, the edit composer inside the
          transcript replaces this composer; keep it unmounted so only one
          composer exists at a time and the draft stays untouched. */}
      {!editingMessageId && (
        <Composer
          key={`composer:${routeConversationId ?? "new-chat"}`}
          persistSelections={
            !location.pathname.startsWith("/projects/") &&
            !new URLSearchParams(location.search).has("project") &&
            conversation?.surface !== "project"
          }
          contextInsertion={contextInsertion}
          onContextInsertionHandled={consumeContextInsertion}
          initialValue={
            composerDraft.current.conversationId === routeConversationId
              ? composerDraft.current.text
              : ""
          }
          onDraftChange={(text) => {
            composerDraft.current = {
              conversationId: routeConversationId,
              text,
            };
          }}
          initialAttachments={
            composerAttachments.current.conversationId === routeConversationId
              ? composerAttachments.current.files
              : []
          }
          onDraftAttachmentsChange={(files) => {
            composerAttachments.current = {
              conversationId: routeConversationId,
              files,
            };
          }}
          onSend={send}
          onStop={stopGeneration}
          conversationId={conversation?.id}
          onEnvironmentConfigured={setDevelopmentConfigured}
          disabled={compacting || loadingConversation}
          generating={sending}
          agents={agents}
          agentId={agentId}
          onAgentChange={selectAgent}
          initialOptions={
            editorSelection?.workspaceId
              ? {
                  ...composerInitialOptions,
                  agentWorkspaceId: editorSelection.workspaceId,
                  agentWorkspaceIds: [editorSelection.workspaceId],
                  repositoryId: editorSelection.repositoryId,
                  repositoryIds: {
                    [editorSelection.workspaceId]: editorSelection.repositoryId
                      ? [editorSelection.repositoryId]
                      : [],
                  },
                }
              : composerInitialOptions
          }
          developmentWorkspaceLoaded={developmentConfigurationLoaded}
          developmentConfigured={developmentConfigured}
          activity={activity}
          onActivityChange={changeActivity}
          availableActivities={availableActivities}
          activityModels={activityModelOptions}
          activityModelIds={activityModelSelection}
          activitySettings={activitySettings}
          enabledActivities={enabledActivities}
          onActivityModelChange={(candidate, value) => {
            activityModelIds.current[candidate] = value;
            setActivityModelSelection((current) => ({
              ...current,
              [candidate]: value,
            }));
            localStorage.setItem(
              "lumen:activity-models",
              JSON.stringify({ ...activityModelIds.current }),
            );
          }}
          onActivitySettingsChange={(candidate, settings) => {
            const nextSettings = settings ?? {};
            if (nextSettings.modelId) {
              activityModelIds.current[candidate] = nextSettings.modelId;
              setActivityModelSelection((current) => ({
                ...current,
                [candidate]: nextSettings.modelId,
              }));
            }
            const next = {
              ...activitySettingsRef.current,
              [candidate]: nextSettings,
            };
            activitySettingsRef.current = next;
            setActivitySettings(next);
            localStorage.setItem(
              "lumen:activity-settings",
              JSON.stringify(next),
            );
          }}
          onToggleActivity={(candidate) => {
            setEnabledActivities((current) => {
              const next = current.includes(candidate)
                ? current.filter((item) => item !== candidate)
                : [...current, candidate];
              localStorage.setItem(
                "lumen:enabled-activities",
                JSON.stringify(next.filter((item) => item !== "ask")),
              );
              if (activity === candidate) setActivity("ask");
              return next.includes("ask") ? next : ["ask", ...next];
            });
          }}
          availableVoices={selected ? modelVoices(selected) : []}
          supportedParameters={selected?.supportedParameters ?? []}
          supportsVision={selected ? modelSupportsVision(selected) : false}
          supportsTools={
            activity === "ask" &&
            Boolean(selected?.capabilities.tools && selected.toolsEnabled)
          }
          thinkingLevels={selected?.thinkingLevels}
          supportsThinking={selected?.supportedParameters.some(
            (parameter) =>
              parameter === "reasoning" || parameter === "reasoning_effort",
          )}
          thinkingLevel={thinkingLevel}
          onThinkingLevelChange={setThinkingLevel}
        />
      )}
      <ConfirmMessageDeleteDialog
        open={Boolean(deleteTarget)}
        busy={sending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
      />
      {renameOpen && (
        <RenameDialog
          title={titleDraft}
          busy={actionBusy}
          onChange={setTitleDraft}
          onCancel={() => setRenameOpen(false)}
          onConfirm={() => void renameConversation()}
        />
      )}
      {shareOpen && (
        <ShareDialog
          info={shareInfo}
          url={shareUrl}
          busy={actionBusy}
          onEnable={() => void enableShare()}
          onDisable={() => void disableShare()}
          onClose={() => setShareOpen(false)}
        />
      )}
      {deleteConversationOpen && (
        <ConfirmDangerDialog
          title={t("Delete this chat?")}
          description="This permanently deletes the conversation, all message versions, and any active share link."
          confirmLabel="Delete chat"
          busy={actionBusy}
          onCancel={() => setDeleteConversationOpen(false)}
          onConfirm={() => void deleteConversation()}
        />
      )}
    </div>
  );
  return (
    <DocumentEditorProvider
      initialArtifactId={
        new URLSearchParams(location.search).get("document") ?? undefined
      }
      onToggleSidebar={
        new URLSearchParams(location.search).get("editorWindow") === "1"
          ? undefined
          : onToggleSidebar
      }
      key={conversation?.id ?? "new-editor"}
      conversationId={conversation?.id}
      onAddToChat={(text) => {
        contextSequence.current += 1;
        setContextInsertion({ id: contextSequence.current, text });
      }}
    >
      <ChatMentionsProvider
        key={conversation?.id ?? "new"}
        conversationId={conversation?.id}
        revision={conversationRevision}
        onOpen={() => setDevelopmentOpen(true)}
      >
        <ConversationWorkspace
          onToggleSidebar={onToggleSidebar}
          conversation={conversation}
          workspaceId={panelWorkspaceId}
          repositoryId={panelRepositoryId}
          onWorkspaceSelectionChange={setEditorSelection}
          onAddToChat={(path, selection) => {
            const text = selection
              ? `File: ${path}\n\n\`\`\`\n${selection}\n\`\`\``
              : `File: ${path}`;
            contextSequence.current += 1;
            setContextInsertion({ id: contextSequence.current, text });
          }}
          developmentPanel={
            developmentOpen && conversation ? (
              <ChatDevelopmentPanel
                conversationId={conversation.id}
                workspaceId={panelWorkspaceId}
                repositoryId={panelRepositoryId}
                developmentConfigured={developmentConfigured}
                approvalsAvailable={user.organizationPermissions.includes(
                  "agent-workspaces:use",
                )}
                usageRevision={usageRevision}
                conversationRevision={conversationRevision}
                onClose={() => setDevelopmentOpen(false)}
              />
            ) : undefined
          }
        >
          {chatPanel}
        </ConversationWorkspace>
      </ChatMentionsProvider>
    </DocumentEditorProvider>
  );
}
